import React, { useContext } from 'react';
import styled from 'styled-components';
import { DesignerCard_UserFragment } from '@99designs/apps/spa/pages/create/components/DesignerCard.generated';
import { BriefContext } from '@99designs/brief';
import { LocalTime, OnlineStatus, PillContainer, isDesignerOnline } from '@99designs/designers';
import { Box, Paragraph } from '@99designs/emu';
import { colors, spaces } from '@99designs/emu/settings';
import { UserAvatar } from '@99designs/users';
import AddDesignerButton from './AddDesignerButton';

interface Props {
    designer: DesignerCard_UserFragment;
    onAddDesigner?: () => void;
}

const DesignerDetailsWrapper = styled.div`
    overflow-y: auto;
`;

const Panel = styled.div<{ withSeparator?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: ${spaces['1.5bu']} ${spaces['2bu']} ${spaces['1.5bu']} ${spaces['1.5bu']};
    border-bottom: ${({ withSeparator }) =>
        withSeparator ? `1px solid ${colors.typography.contentBorder}` : 'none'};
`;

const Meta = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: ${spaces['1.5bu']};

    > * {
        margin-right: ${spaces['1bu']};

        &:last-child {
            margin-right: 0;
        }
    }
`;

function DesignerDetails(props: Props) {
    const { designer, onAddDesigner } = props;
    const isOnlineNow = isDesignerOnline(designer.profile.lastSeenAt);

    const { biography, locales, certifications, categories } = designer.profile;
    const isSquarespaceCertified = certifications.some(
        (certification) => certification === 'squarespace'
    );

    const { brief, totalAllowableDesigners } = useContext(BriefContext);

    return (
        <DesignerDetailsWrapper>
            <Panel withSeparator>
                <Meta>
                    <Box alignSelf="center">
                        <UserAvatar userId={designer.id} size="small" />
                    </Box>
                    <Box>
                        <Paragraph as="div" weight="bold" fontFamily="text" size="regular">
                            {designer.displayName}
                        </Paragraph>
                        <Paragraph as="div" size="small">
                            <OnlineStatus
                                isOnline={isOnlineNow}
                                lastSeenAt={designer.profile.lastSeenAt}
                            />
                        </Paragraph>
                        <Paragraph as="div" size="small">
                            <LocalTime utcOffset={designer.profile.utcOffset} />
                        </Paragraph>
                    </Box>
                </Meta>
                <AddDesignerButton
                    color="grey800"
                    designer={designer}
                    designerSource={'RECOMMENDED'}
                    onAddDesigner={onAddDesigner}
                    brief={brief}
                    totalAllowableDesigners={totalAllowableDesigners}
                />
            </Panel>
            <Panel>
                <Paragraph as="div" size="small">
                    {biography}
                </Paragraph>
                {locales.length > 0 && (
                    <PillContainer
                        title={__('Languages')}
                        items={locales.map((locale) => locale.title)}
                    />
                )}
                {categories.length > 0 && (
                    <PillContainer
                        title={__('Categories')}
                        items={categories.map((category) => category.title)}
                    />
                )}
                {isSquarespaceCertified && (
                    <PillContainer title={__('Certifications')} items={[__('Squarespace')]} />
                )}
            </Panel>
        </DesignerDetailsWrapper>
    );
}

export default DesignerDetails;
